import type { Portfolio } from 'venn-api';
import type { RedistributionMode } from './types';
import { flattenNode, isStrategyNode } from 'venn-utils';
import { sumBy } from 'lodash';

const getWithAmountAddedToFundAllocations = (node: Portfolio, amount: number): Portfolio => {
  if (!isStrategyNode(node)) {
    return {
      ...node,
      allocation: (node.allocation ?? 0) + amount,
      netAssets: (node.netAssets ?? 0) + amount,
      cashBalance: node.cashBalance ? node.cashBalance + amount : 0,
    };
  }
  const updatedChildren = node.children.map((child) => getWithAmountAddedToFundAllocations(child, amount));

  return {
    ...node,
    children: updatedChildren,
    allocation: sumBy(updatedChildren, 'allocation'),
    cashBalance: sumBy(updatedChildren, 'cashBalance'),
    netAssets: sumBy(updatedChildren, 'netAssets'),
  };
};

const getWithScaledAllocations = (node: Portfolio, scalingFactor: number): Portfolio => {
  if (!isStrategyNode(node)) {
    return {
      ...node,
      allocation: (node.allocation ?? 0) * scalingFactor,
      cashBalance: (node.cashBalance ?? 0) * scalingFactor,
      netAssets: (node.netAssets ?? 0) * scalingFactor,
    };
  }
  const updatedChildren = node.children.map((child) => getWithScaledAllocations(child, scalingFactor));

  return {
    ...node,
    children: updatedChildren,
    allocation: sumBy(updatedChildren, 'allocation'),
    cashBalance: sumBy(updatedChildren, 'cashBalance'),
    netAssets: sumBy(updatedChildren, 'netAssets'),
  };
};

/**
 * Get a strategy with allocation increased by the amount passed, with allocations in the subtree modified depending on
 * the redistribution mode.
 *
 * @param strategy - strategy to modify
 * @param amount - capital to add/remove from the strategy (can be negative)
 * @param redistributionMode - 'evenly' if we want to remove the same amount of capital from each fund, or 'prorata'
 *        if we want to remove proportionately to what % of the strategy the fund is today
 */
export const getStrategyWithAddedCapital = ({
  strategy,
  amount,
  redistributionMode,
  getAllocation,
}: {
  strategy: Portfolio;
  amount: number; // can be negative
  redistributionMode: RedistributionMode;
  getAllocation: (portfolio: Portfolio) => number;
}): Portfolio => {
  if (redistributionMode === 'evenly') {
    const allChildFunds: Portfolio[] = flattenNode(strategy).filter((node) => !isStrategyNode(node));
    const perChildAmount: number = amount / allChildFunds.length;
    return getWithAmountAddedToFundAllocations(strategy, perChildAmount);
  }

  const totalAllocation = getAllocation(strategy);

  if (totalAllocation === 0) {
    return { ...strategy };
  }

  return getWithScaledAllocations(strategy, (totalAllocation + amount) / totalAllocation);
};
